import { gql } from '@apollo/client';
import { ReportSectionState } from '@mnd-frontend/graphql-schema';
import { toBoolean } from '@mnd-frontend/ts';
import { Text } from '@mnd-frontend/ui/publish/components';
import { GREY_900 } from '@mnd-frontend/ui/shared/tokens';
import type { Report2PerceptionBenchmarkSentimentOverviewFragment } from '../../../../../generated/types';
import { useReportStaticData, useReportTranslation } from '../../../hooks';
import { SentimentBenchmarkGraph } from '../../charts';
import { Insights, Section, SectionHeader } from '../../common';

export const REPORT2_PERCEPTION_BENCHMARK_SENTIMENT_OVERVIEW_FRAGMENT = gql`
  fragment Report2PerceptionBenchmarkSentimentOverview on ReportPerceptionBenchmarkSentimentOverview {
    id
    title
    state
    metrics {
      brand
      searchProfileIds
      sentiment {
        positiveSentimentPercent
        negativeSentimentPercent
        neutralSentimentPercent
      }
    }
    insights2 {
      id
      title
      insight
      insightType
    }
  }
`;

const getCompetitorsSentiment = (
  metrics: Report2PerceptionBenchmarkSentimentOverviewFragment['metrics'],
  brand: string,
) => {
  return metrics.filter(metric => metric.brand !== brand && metric.searchProfileIds.length > 0);
};

const getHasCompetitorSentiment = (
  metrics: Report2PerceptionBenchmarkSentimentOverviewFragment['metrics'],
) => {
  return (
    metrics
      .map(metric => metric.sentiment)
      .filter(toBoolean)
      .map(
        ({ positiveSentimentPercent, negativeSentimentPercent, neutralSentimentPercent }) =>
          positiveSentimentPercent + negativeSentimentPercent + neutralSentimentPercent,
      )
      .reduce((acc, val) => acc + val, 0) > 0
  );
};

const PerceptionBenchmarkSentimentOverview = ({
  section,
}: {
  section: Report2PerceptionBenchmarkSentimentOverviewFragment;
}) => {
  const { brand } = useReportStaticData();
  const hasCompetitorSentiment = getHasCompetitorSentiment(
    getCompetitorsSentiment(section.metrics, brand),
  );
  const { t } = useReportTranslation();
  return (
    <Section>
      <SectionHeader
        color={GREY_900}
        as="h3"
        variant="reportHeadlineLarge"
        section={section}
        isSubsection
      />
      {!hasCompetitorSentiment && (
        <Text as="p" variant="reportLabelMedium">
          {t('reports_empty_sentiment_section')}
        </Text>
      )}
      <SentimentBenchmarkGraph
        loading={section.state !== ReportSectionState.Done}
        data={section.metrics}
        disabled={!hasCompetitorSentiment}
      />
      <Insights insights={section.insights2} />
    </Section>
  );
};

export default PerceptionBenchmarkSentimentOverview;
